import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme, alpha } from '@mui/material/styles'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

type PatientUnderstandingStepProps = {
  checked: boolean
  onChange: (checked: boolean) => void
}

const patientUnderstandingMessages = {
  title: msg`Evaluate patient understanding`,
  subtitle: msg`Before confirming teleconsultation consent, make sure the patient understands what a teleconsultation is and is comfortable with remote care.`,
  questionsTitle: msg`Suggested questions to ask`,
  attestation: msg`This attestation records that the professional has checked the patient's understanding before the teleconsultation proceeds.`,
  confirmation: msg`I confirm that I have verified the patient understands the purpose, practical conditions, and limits of this teleconsultation.`,
}

const suggestedQuestionMessages = [
  msg`Does the patient know what a teleconsultation is?`,
  msg`Is the patient comfortable speaking with a healthcare professional through a screen?`,
  msg`Has the patient had a teleconsultation before, and how did it go?`,
  msg`Does the patient understand that some situations may require an in-person consultation?`,
]

export function PatientUnderstandingStep({ checked, onChange }: PatientUnderstandingStepProps) {
  const { i18n } = useLingui()
  const theme = useTheme()
  const suggestedQuestions = suggestedQuestionMessages.map((question) => i18n._(question))

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <Box>
        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {i18n._(patientUnderstandingMessages.title)}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mt: 0.5, lineHeight: 1.6 }}>
          {i18n._(patientUnderstandingMessages.subtitle)}
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: '12px', border: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <HelpOutlineIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>
            {i18n._(patientUnderstandingMessages.questionsTitle)}
          </Typography>
        </Stack>
        <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
          {suggestedQuestions.map((question, index) => (
            <Typography key={index} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {question}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: '12px', display: 'flex', gap: 1.5 }}>
        <FactCheckOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mt: 0.25 }} />
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          {i18n._(patientUnderstandingMessages.attestation)}
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            sx={{ color: theme.palette.grey[400], '&.Mui-checked': { color: theme.palette.primary.main }, '& .MuiSvgIcon-root': { fontSize: 24 } }}
          />
        }
        label={
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ lineHeight: 1.6 }}>
            {i18n._(patientUnderstandingMessages.confirmation)}
          </Typography>
        }
        sx={{ alignItems: 'flex-start', mx: 0 }}
      />
    </Stack>
  )
}
